<template>
    <div>
        <SkipToContent />
        <PencilsDownModal
            v-if="isShowingPencilsDownModal"
            @submitExam="submitExam(true)"
        />
        <ExitExamModal
            v-if="showExitExamModal && currentExam"
            :num-answers="numCompleteQuestions"
            :quiz-length="quizLength"
            @close="showExitExamModal = false"
            @submit="submitExam(true)"
            @exit="exitExam"
        />
        <SubmitExamModal
            v-if="showSubmitExamModal && currentExam"
            :num-answers="numCompleteQuestions"
            :quiz-length="quizLength"
            @close="showSubmitExamModal = false"
            @continue="showSubmitExamModal = false"
            @submit="submitExam(true)"
        />
        <QuizContainer
            ref="quizContainer"
            class="mock-exam"
            :is-dark-mode="isDarkMode"
            :custom-main-styles="breakpoint === 'black-bear' ? { height: 'calc(100% - 99px)' } : undefined"
        >
            <template v-if="!isLoadingScoring" #header>
                <div class="mock-exam__exam-info">
                    <ExamMenuCard
                        v-if="currentExam && currentBundle"
                        :exam="currentExam"
                        :bundle-id="currentBundle.objectId"
                        theme="silver"
                        :is-clickable="false"
                        class="mock-exam__exam-info-card"
                    />
                </div>
                <div
                    class="mock-exam__progress"
                >
                    <QuizProgress
                        :num-questions="quizLength"
                        :selected-index="questionIndex + 1"
                        :answered-index-list="answeredIndexList"
                        @barClicked="progressBarClicked"
                        @keydown.left.right.stop.prevent
                    />
                </div>
                <div
                    class="mock-exam__timer"
                    :class="{
                        'mock-exam__timer--low': isLowTime,
                        'mock-exam__timer--ten-minute-break': isTenMinuteBreak
                    }"
                >
                    <Icon
                        class="mock-exam__timer-icon"
                        :class="{
                            'mock-exam__timer-icon--low': isLowTime,
                        }"
                        :type="isLowTime ? 'warning' : 'hourglass'"
                    />
                    {{ timeDisplay }}
                </div>
                <PocketButton
                    class="mock-exam__quit-btn"
                    type="secondary-yellow-dark"
                    :is-dark-mode="isDarkMode"
                    @click="exitExamClicked"
                >
                    Exit Exam
                </PocketButton>
            </template>
            <template
                #question
            >
                <ScoringLoading v-if="isLoadingScoring && !isTenMinuteBreak" />
                <QuestionGrid
                    v-else-if="isShowingQuestionGrid && !isTenMinuteBreak"
                    :questions="activeMockExamQuizQuestions"
                    :answers="answers"
                    :flagged-questions="flaggedQuestions"
                    :is-dark-mode="isDarkMode"
                    @questionClicked="gridQuestionClicked"
                    @close="isShowingQuestionGrid = false"
                />
                <div
                    v-else-if="isTenMinuteBreak && typeOfTimer === 'break' && halfOfExamQsAnswered"
                    class="mock-exam__break-container"
                    :class="{
                        'mock-exam__break-container--last-minute': lastMinuteOfBreak && !isDarkMode,
                        'mock-exam__break-container--dark': isDarkMode,
                    }"
                >
                    <div class="mock-exam__yoga-woman">
                        <img
                            class="mock-exam__yoga-woman-img"
                            src="@/assets/mock-exams/yoga-woman.png"
                            alt=""
                        >
                    </div>
                    <div
                        class="mock-exam__break-header"
                        :class="{
                            'mock-exam__break-header--dark': isDarkMode,
                        }"
                        tabindex="0"
                    >
                        Take a Break
                    </div>
                    <div
                        class="mock-exam__break-description"
                        :class="{
                            'mock-exam__break-description--dark': isDarkMode,
                        }"
                    >
                        The exam timer has paused.
                        Stretch, breathe, and resume when you are ready.
                    </div>
                    <div
                        class="mock-exam__break-timer-container"
                        :class="{
                            'mock-exam__break-timer-container--last-minute': lastMinuteOfBreak,
                            'mock-exam__break-timer-container--dark': isDarkMode,
                        }"
                    >
                        <div
                            class="mock-exam__break-timer"
                            :class="{
                                'mock-exam__break-timer--last-minute': lastMinuteOfBreak && !isDarkMode,
                                'mock-exam__break-timer--last-minute-dark': lastMinuteOfBreak && isDarkMode,
                                'mock-exam__break-timer--dark': !lastMinuteOfBreak && isDarkMode,
                            }"
                        >
                            {{ breakTimeDisplay }}
                        </div>
                    </div>
                    <div class="mock-exam__resume-exam">
                        <PocketButton
                            class="mock-exam__resume-exam-button"
                            :is-dark-mode="isDarkMode"
                            @click="resumeExam"
                        >
                            Resume Exam
                        </PocketButton>
                    </div>
                </div>
                <template v-else-if="!isTenMinuteBreak && !isLoadingScoring && !isShowingQuestionGrid">
                    <Icon
                        v-if="isLoading || !currentExam"
                        v-dark
                        class="mock-exam__loading"
                        type="loading2"
                        :is-dark-mode="isDarkMode"
                    />
                    <Question
                        v-else-if="mode && currentQuestion && currentExam"
                        :key="`${currentQuestion.objectId}-${allowKeyboardShortcuts}-${isOutOfTime}`"
                        class="mock-exam__question"
                        :container-el="quizContainerEl"
                        :question="currentQuestion"
                        :question-number="questionIndex + 1"
                        :quiz-length="quizLength"
                        :quiz-mode="quizModeForQuestion"
                        :image-url-prefix="`${s3Url || ''}/${currentQuestion.compositeKey.toUpperCase()}/`"
                        :hide-answer="true"
                        :show-check-answer="false"
                        :previous-choices="previousChoicesTyped"
                        :previous-matrix-choices="previousMatrixChoicesTyped"
                        :previous-build-list-choices="previousBuildListChoicesTyped"
                        :previous-multi-part-multiple-choice-choices="previousChoicesTyped"
                        :initial-show-answers="false"
                        :allow-keyboard-shortcuts="allowKeyboardShortcuts && !isOutOfTime"
                        :is-dark-mode="isDarkMode"
                        :answer-seed="currentQuestionSeed"
                        :hide-references="currentExam.hideReferences"
                        @close="exitExamClicked"
                        @previousQuestion="clickPrevious"
                        @nextQuestion="clickNext"
                        @checkAnswer="checkAnswer"
                        @selectedChoices="questionAnswered"
                        @submitQuiz="submitExam"
                        @update:showExplanation="explanationViewToggled"
                    >
                        <template
                            #action
                            v-if="(answerSerialSet.size >= questionSerialSet.size) || isNextArrowDisabled"
                        >
                            <PocketButton
                                :is-dark-mode="isDarkMode"
                                :disabled="numCompleteQuestions === 0"
                                @click="submitExam(false)"
                            >
                                Submit Exam
                            </PocketButton>
                        </template>
                    </Question>
                </template>
            </template>
            <template v-if="!isShowingQuestionGrid && !isLoadingScoring" #footer>
                <div
                    v-if="breakpoint !== 'black-bear'"
                    class="mock-exam__grid-toggle"
                    :class="{
                        'mock-exam__grid-toggle--is-break': isTenMinuteBreak,
                    }"
                    tabindex="0"
                    aria-label="All Questions"
                    @click="toggleQuestionGrid"
                    @mousedown.prevent
                >
                    <div class="mock-exam__view-all-questions">
                        <Icon class="mock-exam__list-icon" type="list" />
                        <div class="mock-exam__view-all-questions-label">
                            View All Questions
                        </div>
                    </div>
                </div>
                <div
                    class="mock-exam__controls"
                    :class="{
                        'mock-exam__controls--is-break': isTenMinuteBreak,
                    }"
                >
                    <Icon
                        class="mock-exam__previous-arrow"
                        :class="{
                            'mock-exam__previous-arrow--disabled': isPreviousArrowDisabled,
                        }"
                        :tabindex="isPreviousArrowDisabled ? -1 : 0"
                        type="paginationArrow"
                        role="button"
                        aria-label="Previous question"
                        :aria-disabled="isPreviousArrowDisabled"
                        @click="clickPrevious"
                        @keydown.enter.stop.prevent="clickPrevious"
                        @mousedown.prevent
                    />
                    <div
                        v-if="breakpoint === 'black-bear'"
                        class="mock-exam__grid-toggle-center"
                        tabindex="0"
                        aria-label="All Questions"
                        @click="toggleQuestionGrid"
                        @mousedown.prevent
                    >
                        <Icon
                            class="mock-exam__list-icon-center"
                            type="list"
                        />
                    </div>
                    <FlagToggle
                        :key="`flagToggle_${allowKeyboardShortcuts}`"
                        class="mock-exam__flag-toggle"
                        :is-flagged="isFlagged"
                        :is-dark-mode="isDarkMode"
                        :enable-flag-keyboard-shortcut="allowKeyboardShortcuts"
                        :enable-flag-tooltip="breakpoint !== 'brown-bear' && breakpoint !== 'black-bear'"
                        @toggleFlag="toggleFlag"
                    />
                    <Icon
                        :tabindex="isNextArrowDisabled ? -1 : 0"
                        role="button"
                        aria-label="Next question"
                        :aria-disabled="isNextArrowDisabled"
                        class="mock-exam__next-arrow"
                        :class="{
                            'mock-exam__next-arrow--disabled': isNextArrowDisabled,
                        }"
                        type="paginationArrow"
                        @click="clickNext"
                        @keydown.enter.stop.prevent="clickNext"
                        @mousedown.prevent
                    />
                </div>
                <KeyboardShortcutsButton
                    class="mock-exam__keyboard-shortcuts-btn"
                    :class="{
                        'mock-exam__keyboard-shortcuts-btn--is-break': isTenMinuteBreak,
                    }"
                    :tooltip-theme="
                        (breakpoint === 'polar-bear' || breakpoint === 'grizzly-bear' || breakpoint === 'brown-bear')
                            ? 'leftalign'
                            : undefined
                    "
                    :is-dark-mode="isDarkMode"
                    @keydown.enter.stop
                >
                    <template
                        #keyboardShortcutsModal="{
                            showKeyboardShortcutsModal,
                            closeModalFunc
                        }"
                    >
                        <Portal to="modal">
                            <KeyboardShortcutsModal
                                v-if="showKeyboardShortcutsModal"
                                key="mockExamKeyboardShortcutsModal"
                                :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                                :is-dark-mode="isDarkMode"
                                aria-live="polite"
                                @toggleKeyboardShortcuts="toggleKeyboardShortcuts"
                                @close="closeModalFunc"
                            />
                        </Portal>
                    </template>
                </KeyboardShortcutsButton>
            </template>
        </QuizContainer>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import type { Study } from '@pocketprep/types'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'
import { userModule } from '@/store/user/module'
import { difference, buildListDifference, localISODateString } from '@/utils'
import SubmitExamModal from '@/components/MockExam/SubmitExamModal.vue'
import ExitExamModal from '@/components/MockExam/ExitExamModal.vue'
import PencilsDownModal from '@/components/MockExam/PencilsDownModal.vue'
import QuestionGrid from '@/components/MockExam/QuestionGrid.vue'
import ScoringLoading from '@/components/MockExam/ScoringLoading.vue'
import { screenModule } from '@/store/screen/module'
import SkipToContent from '@/components/SkipToContent.vue'
import { mockExamModule } from '@/store/mockExam/module'
import { analyticsModule } from '@/store/analytics/module'
import { quizModule } from '@/store/quiz/module'

// Question from ui-kit expects a narrower TQuizMode than our app's; cast when passing.
type QuestionQuizMode = 'qotd' | 'quick10' | 'timed' | 'weakest' | 'missed' | 'custom'

// UIKit Question component ChoiceKey props
type TChoiceKey = `${'a' | 'd'}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`
type TMatrixChoiceKey = `${'a' | 'd'}${1 | 2 | 3}_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`
type TBuildListChoiceKey = `a${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`

const route = useRoute()
const router = useRouter()

const {
    FlagToggle,
    KeyboardShortcutsButton,
    Icon,
    ExamMenuCard,
    Button: PocketButton,
    QuizContainer,
    Question,
    QuizProgress,
    KeyboardShortcutsModal,
} = UIKit

const isLoading = ref(true)
const isLoadingScoring = ref(false)
const questionIndex = ref(0)
const timerInterval = ref<ReturnType<typeof setInterval> | null>(null)
const breakTimerInterval = ref<ReturnType<typeof setInterval> | null>(null)
const showExitExamModal = ref(false)
const showSubmitExamModal = ref(false)
const currentExam = ref<null | Study.Class.ExamMetadataJSON | ClientExam>(null)
const currentBundle = ref<null | Study.Class.BundleJSON | ClientBundle>(null)
const fromName = computed(() => (route.meta.fromName as string | undefined) || null)
const isFlagged = ref(false)
const isTogglingFlag = ref(false)
const isShowingQuestionGrid = ref(false)
const isShowingPencilsDownModal = ref(false)
const numOfQuestionsInitiallySkipped = ref<number | null>(null)
const isTenMinuteBreak = ref(false)
const typeOfTimer = ref('exam')
const breakCompleted = ref(false)
const halfOfExamQsAnswered = ref(false)
const quizContainer = ref<InstanceType<typeof UIKit.QuizContainer> | null>(null)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const quizContainerEl = computed(() => {
    const quizContainerRef = quizContainer.value
    return quizContainerRef && '$el' in quizContainerRef ? quizContainerRef.$el : null
})

const mode = computed(() => {
    return activeMockExamQuiz.value?.mode || null
})

// UIKit Question component expects narrower quiz-mode type than Study
const quizModeForQuestion = computed<QuestionQuizMode | undefined>(() => {
    return (mode.value || undefined) as QuestionQuizMode | undefined
})

const activeMockExamQuiz = computed(() => {
    return mockExamModule.getters.getActiveMockExamQuiz()
})

const activeMockExamQuizQuestions = computed(() => {
    return activeMockExamQuiz.value?.questions || []
})

const questionSerialSet = computed(() => {
    return new Set(activeMockExamQuizQuestions.value.map(question => question.serial))
})

const answers = computed(() => {
    return activeMockExamQuiz.value?.answers || []
})

const numCompleteQuestions = computed(() => {
    return activeMockExamQuizQuestions.value?.filter(q => isQuestionComplete(q.serial))?.length || 0
})

const answerSerialSet = computed(() => {
    return new Set(answers.value.map(answer => answer.questionSerial))
})

const numOfAllQuestionViews = computed(() => {
    return activeMockExamQuiz.value?.numOfAllQuestionViews || 0
})

const timeLimitSeconds = computed(() => {
    return (activeMockExamQuiz.value?.timeLimit || 0) * 60
})

const durationSeconds = computed(() => {
    return activeMockExamQuiz.value?.durationSeconds || 0
})

const breakDurationSeconds = computed(() => {
    return activeMockExamQuiz.value?.breakDurationSeconds || 0
})

const secondsRemaining = computed(() => {
    return timeLimitSeconds.value - durationSeconds.value
})

const breakSecondsRemaining = computed(() => {
    return 600 - breakDurationSeconds.value
})

const startedAt = computed(() => {
    return activeMockExamQuiz.value?.startedAt
})

const currentQuestion = computed(() => {
    return activeMockExamQuizQuestions.value[questionIndex.value] || null
})

const currentQuestionSeed = computed(() => {
    return activeMockExamQuiz.value?.answerSeeds?.[questionIndex.value] || null
})

const quizLength = computed(() => {
    return activeMockExamQuizQuestions.value.length
})

const timeDisplay = computed(() => {
    if (!secondsRemaining.value) {
        return '0:00:00'
    }
    const isExtraTime = secondsRemaining.value < 0

    const hourSeconds = Math.abs(secondsRemaining.value)
    const hours = Math.floor(hourSeconds / 3600)
    const minuteSeconds = hourSeconds % 3600
    const minutes = Math.floor(minuteSeconds / 60)
    const paddedMinutes = String(minutes).padStart(2, '0')
    const seconds = minuteSeconds % 60
    const paddedSeconds = String(seconds).padStart(2, '0')

    return `${isExtraTime ? '+ ' : ''}${hours}:${paddedMinutes}:${paddedSeconds}`
})

const breakTimeDisplay = computed(() => {
    if (!breakSecondsRemaining.value) {
        return '0:00:00'
    }

    const hourSeconds = Math.abs(breakSecondsRemaining.value)
    const minuteSeconds = hourSeconds % 3600
    const minutes = Math.floor(minuteSeconds / 60)
    const paddedMinutes = String(minutes).padStart(2, '0')
    const seconds = minuteSeconds % 60
    const paddedSeconds = String(seconds).padStart(2, '0')
    return `${paddedMinutes}:${paddedSeconds}`
})

/**
 * The remaining seconds at which point the timer should turn red.
 * (10% of the time limit rounded up to closest 5 minutes)
 */
const lowTimeThreshold = computed(() => {
    const timeLimitMinutes = activeMockExamQuiz.value?.timeLimit || 0
    const tenPercentTime = timeLimitMinutes * 0.1
    const roundedUpFiveMins = Math.ceil(tenPercentTime / 5) * 5
    return roundedUpFiveMins * 60   // seconds
})

const isLowTime = computed(() => {
    return (0 <= secondsRemaining.value) && (secondsRemaining.value <= lowTimeThreshold.value)
})

const isOutOfTime = computed(() => {
    return activeMockExamQuiz.value
        && !activeMockExamQuiz.value.getsExtraTime
        && (durationSeconds.value >= timeLimitSeconds.value)
})

const lastMinuteOfBreak = computed(() => {
    return breakDurationSeconds.value >= 540
})

const isBreakOver = computed(() => {
    return activeMockExamQuiz.value
        && (breakDurationSeconds.value >= 600)
})

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

const flaggedQuestions = computed(() => {
    return userExamMetadataModule.getters.getFlaggedQuestions()
        .filter(serial => questionSerialSet.value.has(serial))
})

const answeredIndexList = computed(() => {
    return activeMockExamQuizQuestions.value.reduce<number[]>((acc, question, index) => {
        if (answerSerialSet.value.has(question.serial)) {
            acc.push(index + 1)
        }
        return acc
    }, [])
})

const currentAnswer = computed(() => {
    return answers.value.find(ans => currentQuestion.value?.serial === ans.questionSerial) || null
})

const previousChoices = computed(() => {
    return currentAnswer.value?.selectedChoices || []
})

// Question component expects literal union types; cast from string[] for type compatibility
const previousChoicesTyped = computed(() => {
    // non matrix and build list questions
    return previousChoices.value as TChoiceKey[]
})

const previousMatrixChoicesTyped = computed(() => {
    return previousChoices.value as TMatrixChoiceKey[]
})

const previousBuildListChoicesTyped = computed(() => {
    return previousChoices.value as TBuildListChoiceKey[]
})

const isPreviousArrowDisabled = computed(() => {
    return questionIndex.value === 0
})

const isNextArrowDisabled = computed(() => {
    return (questionIndex.value >= (quizLength.value - 1))
})

const quizSettings = computed(() => {
    return userModule.state.user?.quizSettings || null
})

const allowKeyboardShortcuts = computed(() => {
    return !!quizSettings.value?.enableKeyboardShortcuts
})

const percentQuestionBankCompleted = computed(() => {
    const totalAnswers = Object.keys(quizModule.getters.getLatestAnswers({ questionFilter: 'all' })).length || 0
    const totalQuestions = (currentExam.value?.itemCount || 0) - (currentExam.value?.archivedCount || 0)

    return Math.round((totalAnswers / totalQuestions) * 100)
})

const percentageAnswered = computed(() => {
    return Math.round((answerSerialSet.value.size / questionSerialSet.value.size) * 100)
})

const priorMockExamsCompleted = computed(() => {
    let priorMockExamsCompletedVar = 0     // Only for Mock Exams connected to the current ExamMetadata
    quizModule.state.quizzes.forEach(quiz => {
        if (quiz.mode === 5 && quiz.examGuid === currentExam.value?.examGuid) {
            priorMockExamsCompletedVar++
        }
    })
    return priorMockExamsCompletedVar
})

const mockExamFinishedPayload = computed(() => {
    return {
        examName: currentExam.value?.nativeAppName || 'Unknown',
        percentageAnswered: percentageAnswered.value,
        minutesInExam: Math.round(durationSeconds.value / 60),
        choseExtraTime: activeMockExamQuiz.value?.getsExtraTime || false,
        numOfAllQuestionViews: numOfAllQuestionViews.value,
        platform: 'Web',
        percentQuestionBankCompleted: percentQuestionBankCompleted.value,
        priorMockExamsCompleted: priorMockExamsCompleted.value,
    }
})

onMounted(async () => {
    if (!activeMockExamQuiz.value) {
        router.push({ name: 'study' })
        return
    }

    // Restore place in any previously saved quiz
    questionIndex.value = activeMockExamQuiz.value.currentQuestionIndex

    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
    ])

    currentExam.value = examMetadataModule.getters.getCurrentExamMetadata()
    currentBundle.value = bundleModule.getters.getCurrentBundle() || null

    const questionSerial = currentQuestion.value?.serial
    isFlagged.value = !!(questionSerial && flaggedQuestions.value.includes(questionSerial))

    if (isOutOfTime.value) {
        isShowingPencilsDownModal.value = true
    }

    const activeMockExamQuizVal = activeMockExamQuiz.value
    // check which timer to start if a user has paused and restarted an exam
    if (activeMockExamQuizVal) {
        if (activeMockExamQuizVal.timerType === 'exam') {
            examTimer()
        }
        if (activeMockExamQuizVal.timerType === 'break') {
            typeOfTimer.value = 'break'
            isTenMinuteBreak.value = true
            halfOfExamQsAnswered.value = true
            breakTimer()
        }
    } else {
        examTimer()
    }

    isLoading.value = false
})

onBeforeUnmount(() => {
    if (timerInterval.value) {
        clearInterval(timerInterval.value)
    }

    if (breakTimerInterval.value) {
        clearInterval(breakTimerInterval.value)
    }

    isTenMinuteBreak.value = false
})

const examTimer = () => {
    timerInterval.value = setInterval(() => {
        const activeMockExamQuizVal = activeMockExamQuiz.value

        if (activeMockExamQuizVal) {
            const durationSecondsVal = activeMockExamQuizVal.durationSeconds || 0
            if (!isOutOfTime.value) {
                activeMockExamQuizVal.durationSeconds = durationSecondsVal + 1
                // If user is viewing All Questions, track the time spent in that view
                if (isShowingQuestionGrid.value) {
                    if (!activeMockExamQuizVal.secondsInAllQuestionsView) {
                        activeMockExamQuizVal.secondsInAllQuestionsView = 1
                    } else if (isShowingQuestionGrid.value) {
                        activeMockExamQuizVal.secondsInAllQuestionsView += 1
                    }
                }
                mockExamModule.actions.updateActiveMockExamQuiz(activeMockExamQuizVal)
                // out of time
                if (timerInterval.value && isOutOfTime.value) {
                    clearInterval(timerInterval.value)
                }

                // half way through exam, start break timer
                if (timerInterval.value
                    && typeOfTimer.value === 'exam'
                    && !breakCompleted.value
                    && halfOfExamQsAnswered.value
                ) {
                    activeMockExamQuizVal.timerType = 'break'
                    mockExamModule.actions.updateActiveMockExamQuiz(activeMockExamQuizVal)
                    typeOfTimer.value = 'break'
                    isTenMinuteBreak.value = true
                    clearInterval(timerInterval.value)
                    breakTimer()
                }
            }
        }
    }, 1000)
}

// 10 Minute Break Timer
const breakTimer = () => {
    breakTimerInterval.value = setInterval(() => {
        const activeMockExamQuizVal = activeMockExamQuiz.value
        if (activeMockExamQuizVal) {
            const breakDurationSecondsVal = activeMockExamQuizVal.breakDurationSeconds || 0

            if (!isBreakOver.value) {
                activeMockExamQuizVal.breakDurationSeconds = breakDurationSecondsVal + 1
                mockExamModule.actions.updateActiveMockExamQuiz(activeMockExamQuizVal)

                // 10 minute break is over, resume exam timer
                if (breakTimerInterval.value
                    && typeOfTimer.value === 'break'
                    && isBreakOver.value
                ) {
                    activeMockExamQuizVal.timerType = 'exam'
                    mockExamModule.actions.updateActiveMockExamQuiz(activeMockExamQuizVal)
                    typeOfTimer.value = 'exam'
                    isTenMinuteBreak.value = false
                    breakCompleted.value = true
                    clearInterval(breakTimerInterval.value)
                    examTimer()
                }
            }
        }
    }, 1000)
}

const isHalfWay = () => {
    const activeMockExamQuizVal = activeMockExamQuiz.value
    if (activeMockExamQuizVal?.questions.length && activeMockExamQuizVal?.questions.length > 1) {
        const halfTotalQs = Math.ceil((activeMockExamQuizVal?.questions.length || 0) / 2)
        const halfwayThroughQs = halfTotalQs === numCompleteQuestions.value ? true : false
        halfOfExamQsAnswered.value = halfwayThroughQs
    }
}

const isQuestionComplete = (questionSerial: string) => {
    const question = activeMockExamQuizQuestions.value.find(q => q.serial === questionSerial)
    const answer = answers.value.find(a => a.questionSerial === questionSerial)
    if (!question || !answer) {
        return false
    }

    // Matrix questions are complete if each row has a selected choice
    if ([ 'Matrix Radio Button', 'Matrix Checkbox' ].includes(question.type)) {
        const answeredRowNums = answer.selectedChoices.map(choice =>
            Number(choice.split('_')[0].slice(1))    // a1_2 -> 1
        )
        const matrixRows = question.matrixChoiceLayout || []
        const isEveryRowAnswered = matrixRows.every((row, index) => {
            const rowNum = index + 1
            const hasSelectedChoiceForRow = answeredRowNums.includes(rowNum)
            return hasSelectedChoiceForRow
        })
        return isEveryRowAnswered
    }

    // MPMC questions are answered if each part (mpmc label) has a selected choice
    if (question.type === 'Multi-Part Multiple Choice' && question.mpmcLabels) {
        const selectedChoices = answer.selectedChoices
        const mpmcLabels = question.mpmcLabels
        const selectedMPMCLabelIndexes: number[] = []

        selectedChoices.forEach(choice => {
            const mpmcChoice = question.choices.find(c => c.id === choice)
            if (
                mpmcChoice?.labelIndex !== undefined
                && !selectedMPMCLabelIndexes.includes(mpmcChoice?.labelIndex)
            ) {
                selectedMPMCLabelIndexes.push(mpmcChoice?.labelIndex)
            }
        })

        return mpmcLabels.length === selectedMPMCLabelIndexes.length
    }

    // Other question types are complete if they have 1 or more selected choice
    return answer.selectedChoices.length > 0
}

watch(currentQuestion, () => {
    currentQuestionChanged()
})

const currentQuestionChanged = () => {
    isFlagged.value = !!currentQuestion.value && flaggedQuestions.value.includes(currentQuestion.value.serial)
}

const exitExamClicked = async () => {
    if (numCompleteQuestions.value) {
        showExitExamModal.value = true
    } else {
        await exitExam()
    }
}

const exitExam = async () => {
    if (currentExam.value) {
        await analyticsModule.actions.trackEvent('Mock_Exam_Finished_2', {
            ...mockExamFinishedPayload.value,
            abandoned: true,  // If user exited early
        })
    }

    mockExamModule.actions.updateActiveMockExamQuiz(null)
    if (!fromName.value || fromName.value === 'mock-exam-intro') {
        router.push({
            name: 'study',
        })
    } else {
        router.back()
    }
    isTenMinuteBreak.value = false
}

const resumeExam = () => {
    const activeMockExamQuizVal = activeMockExamQuiz.value

    if (breakTimerInterval.value) {
        clearInterval(breakTimerInterval.value)
    }

    if (activeMockExamQuizVal) {
        activeMockExamQuizVal.timerType = 'exam'
        mockExamModule.actions.updateActiveMockExamQuiz(activeMockExamQuizVal)
    }

    typeOfTimer.value = 'exam'
    isTenMinuteBreak.value = false
    breakCompleted.value = true
    examTimer()
}

const clickPrevious = () => {
    if (questionIndex.value > 0) {
        questionIndex.value--
    }
    // check to see if the user is halfway through their questions
    // do not call again once break has been completed
    if (breakCompleted.value === false && halfOfExamQsAnswered.value === false) {
        isHalfWay()
    }
}

const clickNext = () => {
    if (questionIndex.value < (quizLength.value - 1)) {
        questionIndex.value++

        // move focus back to top
        nextTick(() => {
            const headEl = document.querySelector('.uikit-quiz-container__header') as HTMLElement
            const focusableEls = Array.from<HTMLElement>(headEl.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            ))
            focusableEls[focusableEls.length - 1]?.focus()
            focusableEls[focusableEls.length - 1]?.blur()
        })
    }

    // check to see if the user is halfway through their questions
    // do not call again once break has been completed
    if (breakCompleted.value === false && halfOfExamQsAnswered.value === false) {
        isHalfWay()
    }
}

const progressBarClicked = (questionNumber: number) => {
    questionIndex.value = questionNumber - 1
    // check to see if the user is halfway through their questions
    // do not call again once break has been completed
    if (breakCompleted.value === false && halfOfExamQsAnswered.value === false) {
        isHalfWay()
    }
}

const gridQuestionClicked = (questionIndexVal: number) => {
    questionIndex.value = questionIndexVal
}

const checkAnswer = (answer: Study.Cloud.IQuizAnswer) => {
    questionAnswered(answer, true)
}

const questionAnswered = (answer: Study.Cloud.IQuizAnswer, checked?: boolean) => {
    if (!activeMockExamQuiz.value) {
        throw new Error('A quiz is active, but no activeMockExamQuiz was found')
    }

    // Do not allow answering a question if the quiz is over
    if (isOutOfTime.value) {
        return
    }

    const answersVal = answers.value
    const existingAnswer = answersVal.find(ans => ans.questionSerial === answer.questionSerial)

    if (currentQuestion.value.type === 'Build List' && (
        existingAnswer
        && !checked
        && !buildListDifference(existingAnswer.selectedChoices, answer.selectedChoices).length
        && !buildListDifference(answer.selectedChoices, existingAnswer.selectedChoices).length
    ) || (
        !existingAnswer
        && !answer.selectedChoices.length
    )) {
        // If question component emits selected choices order that we already have, don't make any updates
        // When a user sees a build list there will already be a "selected order" so we want to note
        // if the order is different
        return
    } else if (currentQuestion.value.type !== 'Build List' && (
        existingAnswer
        && !checked
        && !difference(existingAnswer.selectedChoices, answer.selectedChoices).length
        && !difference(answer.selectedChoices, existingAnswer.selectedChoices).length
    ) || (
        !existingAnswer
        && !answer.selectedChoices.length
    )) {
        // If question component emits selected choices that we already have or are empty, don't make any updates
        return
    }

    if (existingAnswer && !answer.selectedChoices.length) {
        // If the user has removed their selections, remove the answer from the list
        const answerIndex = answersVal.indexOf(existingAnswer)
        answersVal.splice(answerIndex, 1)
    } else if (existingAnswer) {
        // If we have new selected choices for an existing answer, update them on the answer object
        existingAnswer.selectedChoices = answer.selectedChoices
        existingAnswer.isCorrect = answer.isCorrect
        if (checked) {
            existingAnswer.checked = !!checked
        }
    } else {
        // If we have selected choices for a new answer, update the answers list
        answersVal.push({
            ...answer,
            checked: !!checked,
        })
    }

    mockExamModule.actions.updateActiveMockExamQuiz({
        ...activeMockExamQuiz.value,
        answers: answersVal,
    })
}

const toggleQuestionGrid = () => {
    isShowingQuestionGrid.value = !isShowingQuestionGrid.value

    // Track number of times user views All Questions
    if (isShowingQuestionGrid.value && activeMockExamQuiz.value) {
        mockExamModule.actions.updateActiveMockExamQuiz({
            ...activeMockExamQuiz.value,
            numOfAllQuestionViews: numOfAllQuestionViews.value + 1,
        })
    }
}

const toggleFlag = async () => {
    // Prevent if already processing toggleQuestionFlag call
    if (isTogglingFlag.value) {
        return
    }

    isTogglingFlag.value = true
    isFlagged.value = !isFlagged.value
    const serial = currentQuestion.value?.serial

    try {
        await userExamMetadataModule.actions.toggleQuestionFlag(serial)
    } catch (e) {
        // noop
    } finally {
        isFlagged.value = !!(serial && flaggedQuestions.value.includes(serial))
        isTogglingFlag.value = false
    }
}

const toggleKeyboardShortcuts = (newVal: boolean) => {
    userModule.actions.updateQuizSettings({
        enableKeyboardShortcuts: newVal,
    })
}

const submitExam = async (skipConfirm?: boolean) => {
    showExitExamModal.value = false
    showSubmitExamModal.value = false
    isTenMinuteBreak.value = false

    if (!currentExam.value) {
        throw new Error('MockExam.submitExam: No current exam found')
    }

    if (!activeMockExamQuiz.value) {
        throw new Error('MockExam.submitExam: No active mock exam quiz found')
    }

    if (!skipConfirm) {
        showSubmitExamModal.value = true
        return
    }

    const loadingStartTime = Date.now()
    isLoadingScoring.value = true

    await analyticsModule.actions.trackEvent('Mock_Exam_Finished_2', {
        ...mockExamFinishedPayload.value,
        abandoned: false,  // If user exited early
    })

    // If the user skipped any questions, we need to mark them as incorrect with no selected choice
    // Also, we want to make sure the answers are in the same order that the questions were presented
    const fullAnswers = activeMockExamQuizQuestions.value.map(question => {
        const answerForQuestion = answers.value.find(answer => answer.questionSerial === question.serial)

        return answerForQuestion || {
            questionSerial: question.serial,
            selectedChoices: [],
            isCorrect: false,
        }
    })

    const recordQuizResponse = await mockExamModule.actions.recordMockExamQuiz({
        answers: fullAnswers,
        durationSeconds: durationSeconds.value,
        examGuid: currentExam.value.examGuid,
        mode: 5,
        mockExamId: activeMockExamQuiz.value.mockExam.objectId,
        platform: 'Web',
        appBundleId: 'study.pocketprep.com',
        startedAt: startedAt.value || localISODateString(),
        // Removing this because readiness-report experiment v1 is done, but may use for v2 in 2026
        // includeReadiness: true,
    })

    // Make sure the loading screen is visible for a minimum time (2 seconds)
    const loadingDuration = Date.now() - loadingStartTime
    const minimumLoadingDuration = 2000
    if (loadingDuration < minimumLoadingDuration) {
        await new Promise(res => setTimeout(res, minimumLoadingDuration - loadingDuration))
    }

    isTenMinuteBreak.value = false

    router.push({
        name: 'quiz-result',
        params: { quizId: recordQuizResponse.quiz.objectId },
        query: {
            from: fromName.value || null,
        },
    })
    isLoading.value = false
}

watch(questionIndex, (questionIndexVal) => {
    questionIndexChanged(questionIndexVal)
})

const questionIndexChanged = (questionIndexVal: number) => {
    // When user FIRST reaches the last question, track how many questions they skipped
    if (questionIndexVal === questionSerialSet.value.size - 1 && numOfQuestionsInitiallySkipped.value === null) {
        // The number of skipped questions should not include the last question (which is always unanswered)
        numOfQuestionsInitiallySkipped.value = questionSerialSet.value.size - answerSerialSet.value.size - 1
    }

    if (activeMockExamQuiz.value) {
        mockExamModule.actions.updateActiveMockExamQuiz({
            ...activeMockExamQuiz.value,
            currentQuestionIndex: questionIndexVal,
        })
    }
}

watch(isOutOfTime, (isOutOfTimeVal: boolean | null) => {
    isOutOfTimeChanged(isOutOfTimeVal)
})

const isOutOfTimeChanged = (isOutOfTimeVal: boolean | null) => {
    if (isOutOfTimeVal) {
        showExitExamModal.value = false
        showSubmitExamModal.value = false
        isShowingPencilsDownModal.value = true
    }
}

const explanationViewToggled = (showExplanation: boolean) => {
    if (showExplanation) {
        analyticsModule.actions.amplitudeTrack(
            'Explanation Opened',
            {
                examGuid: currentExam.value?.examGuid,
                bundleId: currentBundle.value?.objectId || '',
                serial: currentQuestion.value.serial,
                quizMode: 5,
                quizPlatform: 'Web',
                isSharedQotd: false,
                isReviewMode: false,
            }
        )
    }
}
</script>

<style lang="scss" scoped>
.mock-exam {
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;

    &__exam-info {
        position: absolute;
        left: -10px;
        width: 320px;

        @include breakpoint(brown-bear) {
            width: 285px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__exam-info-card {
        max-width: 100%;
    }

    &__progress {
        position: absolute;
        left: 50%;
        max-width: 768px;
        width: calc(40% + 130px);
        transform: translateX(-50%);

        @include breakpoint(polar-bear) {
            width: calc(40% + 45px);
        }

        @include breakpoint(brown-bear) {
            max-width: 280px;
            width: calc(36% + 38px);
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__timer {
        display: flex;
        align-items: center;
        position: absolute;
        right: 121px;

        &--low {
            color: $rosa;
        }

        &--ten-minute-break {
            color: rgb(255, 255, 255, 0.6);
        }

        @include breakpoint(black-bear) {
            right: auto;
            left: 50%;
            transform: translateX(-50%);
        }
    }

    &__timer-icon {
        margin-right: 4px;

        &--low {
            width: 21px;
            height: 21px;
            margin-right: 6px;
        }
    }

    &__quit-btn {
        position: absolute !important;
        right: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__break-container {
        position: absolute;
        background-color: $barely-green;
        top: 0;
        left: -65px;
        width: calc(100% + 130px);
        height: calc(100% + 65px);
        color: $brand-black;
        overflow: hidden;
        z-index: 1;

        @include breakpoint(polar-bear) {
            left: -23px;
            width: calc(100% + 45px);
        }

        @include breakpoint (brown-bear) {
            left: -19px;
            width: calc(100% + 38px);
        }

        @include breakpoint (black-bear) {
            left: 0;
            width: 100%;
        }

        &--last-minute {
            background-color: $barely-red;
        }

        &--dark {
            background-color: $charcoal;
        }
    }

    &__yoga-woman {
        margin: auto;
        text-align: center;
        padding-top: 60px;
    }

    &__yoga-woman-img {
        height: 122px;
        width: 199px;
    }

    &__break-header {
        width: 375px;
        height: 25px;
        color: $brand-black;
        font-size: 18px;
        line-height: 22px;
        text-align: center;
        font-weight: 600;
        padding-top: 32px;
        padding-bottom: 8px;
        margin: auto;

        &--dark {
            color: $white;
        }

        &:focus {
            outline: none;
        }
    }

    &__break-description {
        width: 375px;
        height: 52px;
        color: $ash;
        font-size: 15px;
        line-height: 22px;
        text-align: center;
        padding-bottom: 16px;
        margin: auto;

        &--dark {
            color: $white;
        }

        &:focus {
            outline: none;
        }
    }

    &__break-timer-container {
        background-color: rgb(20, 202, 158, 0.2);
        border-radius: 6px;
        width: 290px;
        height: 138px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: auto;

        &--last-minute {
            background-color: rgb(255, 71, 71, 0.2);
        }
    }

    &__break-timer {
        color: $scifi-takeout;
        font-size: 80px;
        font-weight: 600;
        line-height: 96px;

        &--last-minute {
            color: $pepper;
        }

        &--last-minute-dark {
            color: $rosa;
        }

        &--dark {
            color: $jungle-green;
        }
    }

    &__resume-exam {
        margin: auto;
        text-align: center;
        padding-top: 32px;
    }

    &__loading {
        position: absolute;
        left: 50%;
        top: 25%;
        transform: translateX(-50%) scale(1.5);
    }

    &__question {
        position: relative;
        height: 100%;
    }

    &__grid-toggle {
        display: flex;
        position: relative;
        outline: none;
        left: 6px;
        cursor: pointer;

        &--is-break {
            display: none;
        }

        @media (hover: hover) {
            &:hover {
                color: $banana-bread;
            }
        }

        &:focus::before {
            content: '';
            position: absolute;
            border: 1px solid $butterscotch;
            width: 34px;
            height: 32px;
            top: -4px;
            left: -4px;
            border-radius: 4px;
            box-sizing: border-box;
        }
    }

    &__list-icon {
        width: 28px;
        height: 28px;
    }

    &__view-all-questions {
        display: flex;
        position: relative;

        &:hover {
            color: $banana-bread;
        }
    }

    &__view-all-questions-label {
        font-size: 15px;
        font-weight: 600;
        line-height: 18px;
        padding-left: 15px;
        margin: auto;
    }

    &__controls {
        display: flex;
        align-items: center;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);

        &--is-break {
            display: none;
        }

        @include breakpoint(black-bear) {
            width: 304px;
        }
    }

    &__previous-arrow,
    &__next-arrow {
        &--disabled {
            opacity: 0.35;
        }

        &:not(&--disabled) {
            cursor: pointer;

            @media (hover: hover) {
                &:hover {
                    color: $banana-bread;
                }
            }
        }

        &:focus {
            outline: none;
            color: $banana-bread;
        }
    }

    &__previous-arrow {
        transform: rotate(180deg);
    }

    &__grid-toggle-center {
        position: relative;
        display: inline-flex;
        outline: none;
        margin-left: 40px;
        cursor: pointer;

        @media (hover: hover) {
            &:hover {
                color: $banana-bread;
            }
        }

        &:focus::before {
            content: '';
            position: absolute;
            border: 1px solid $butterscotch;
            width: 34px;
            height: 32px;
            top: -4px;
            left: -4px;
            border-radius: 4px;
            box-sizing: border-box;
        }
    }

    &__list-icon-center {
        width: 28px;
        height: 28px;
    }

    &__flag-toggle {
        margin: 0 80px;

        @include breakpoint(black-bear) {
            margin: 0 40px 0 60px;
        }
    }

    &__keyboard-shortcuts-btn {
        position: absolute;
        right: 8px;

        &--is-break {
            display: none;
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    :deep(.uikit-quiz-container__footer) {
        z-index: 0;
    }
}
</style>
